// @ts-nocheck
/**
 * In-page Fast Simon search: submitting the header search paints Fast Simon's
 * real results app into a full-page overlay, no document load. Clearing the box
 * closes it.
 *
 * Typing alone does not open it - the Fast Simon autocomplete dropdown owns the
 * as-you-type preview now. See SEARCH_WHILE_TYPING below.
 *
 * Fast Simon reads its query from ?q= only, so it is steered by rewriting the
 * URL with pushState and firing popstate - the same mechanism
 * 'fast-simon-collection-header' uses. The URL changes; no navigation happens.
 *
 * Pages that already host a SERP (collections, the search page) render no
 * overlay and re-query that instance instead.
 */
(function () {
  'use strict';

  /* FAST_SEARCH_HANDLE in 'isp-smart-navigation', and the path pushed into the URL. */
  var SEARCH_PAGE = '/pages/search-results';

  var HOME_PAGE = '/';

  /* Whether typing opens the results by itself.

     OFF: results open only on a deliberate action - submitting the box (Enter or
     the search button) or picking something out of the Fast Simon autocomplete
     dropdown, which navigates on its own. Typing shows the dropdown and nothing
     else.

     The dropdown is why this is off. It arrives with the Fast Simon app embed
     (the `autocomplete` app block in settings_data.json) and covers the top of
     the viewport as the shopper types, so painting the full results app
     underneath it at the same time put two live result sets on screen at once,
     each with its own idea of the query.

     MIN_CHARS and DEBOUNCE_MS below apply only while this is on. */
  var SEARCH_WHILE_TYPING = false;

  var MIN_CHARS = 3;
  var DEBOUNCE_MS = 600;

  /* Longer than DEBOUNCE_MS: clearing is usually the first half of "select all,
     retype", and closing mid-retype would be infuriating. */
  var CLEAR_DELAY_MS = 1000;

  var RENDER_TIMEOUT_MS = 5000;

  /* Off by default: "no navigation" is the point of this feature, and a silent
     redirect also hides why the overlay failed. Failures stay on screen and report
     through the logs below instead. */
  var NAVIGATE_ON_FAILURE = false;

  /* Fast Simon's app is a remote bundle, so these logs are the only way to tell
     "never mounted" from "mounted but never painted". */
  var DEBUG = true;

  function log() {
    if (!DEBUG || !window.console) return;
    var args = Array.prototype.slice.call(arguments);
    args.unshift('[fs-search-overlay]');
    console.log.apply(console, args);
  }

  /* Both entry points. `.--desktop` is display:none below 767px, so without the
     drawer selector every mobile search fell through to Shopify's own
     <predictive-search>. */
  var INPUT_SELECTOR =
    '.header__search-form input[name="q"], #search-drawer input[name="q"]';
  var OPEN_CLASS = 'fs-search-open';

  var overlay = null;
  var mountPoint = null;
  var template = null;
  var lookedUp = false;

  function findElements() {
    if (lookedUp) return !!(overlay && mountPoint && template);
    lookedUp = true;

    overlay = document.getElementById('fs-search-overlay');
    mountPoint = document.getElementById('fs-search-overlay-mount');
    template = document.getElementById('fs-search-overlay-template');

    return !!(overlay && mountPoint && template);
  }

  /* False on pages that render no overlay - collections and the search page. */
  function overlayAvailable() {
    return findElements();
  }

  var mounted = false;
  var isOpen = false;

  /* URL and scroll position from before the search began, restored on close.
     Scroll is captured explicitly because `body { overflow: hidden }` can clamp
     it while the overlay is open. */
  var urlBeforeSearch = null;
  var scrollBeforeSearch = null;

  /* Scripts cloned out of a <template> never run - the parser flags them as
     already-executed on creation. Rebuilding each as a fresh element is what
     starts the bundle; document order is kept so the inline config evaluates
     before the module that reads it. */
  function reviveScripts(fragment) {
    var scripts = fragment.querySelectorAll('script');

    Array.prototype.forEach.call(scripts, function (original) {
      var fresh = document.createElement('script');

      Array.prototype.forEach.call(original.attributes, function (attr) {
        fresh.setAttribute(attr.name, attr.value);
      });
      fresh.textContent = original.textContent;

      original.parentNode.replaceChild(fresh, original);
    });

    return scripts.length;
  }

  /* The SERP snippet ships a global `#shopify-section-footer { display: none }`.
     The footer is inside <main> and already hidden here, so left unscoped the rule
     would keep it hidden for the rest of the session. Rescoping the clone avoids
     editing the auto-generated snippet. */
  function scopeFooterRule(fragment) {
    var styles = fragment.querySelectorAll('style');

    Array.prototype.forEach.call(styles, function (style) {
      if (style.textContent.indexOf('#shopify-section-footer') === -1) return;

      style.textContent = style.textContent.replace(
        /#shopify-section-footer/g,
        'body.' + OPEN_CLASS + ' #shopify-section-footer'
      );
    });
  }

  function mountSerp() {
    if (mounted) return true;
    if (!findElements()) return false;

    var fragment = template.content.cloneNode(true);
    scopeFooterRule(fragment);
    var scriptCount = reviveScripts(fragment);
    mountPoint.appendChild(fragment);

    mounted = true;
    log(
      'Mounted SERP into overlay.',
      scriptCount,
      'script(s) revived;',
      '#fast-simon-serp-app present:',
      !!document.getElementById('fast-simon-serp-app')
    );
    return true;
  }

  /* Path is left as-is - search from the home page and you stay there - and only
     ?q= is added, the sole channel Fast Simon has for receiving a term. It cannot
     be stripped again afterwards: every facet click and page change re-reads the
     URL. */
  function inPlaceUrl(term) {
    return window.location.pathname + '?q=' + encodeURIComponent(term);
  }

  /* Real navigation target, used only when the overlay cannot be used. */
  function fallbackUrl(term) {
    return SEARCH_PAGE + '?q=' + encodeURIComponent(term);
  }

  function currentQuery() {
    return new URLSearchParams(window.location.search).get('q') || '';
  }

  function onSearchPage() {
    return window.location.pathname === SEARCH_PAGE;
  }

  /* Results painted under the mobile drawer would be invisible. Dismissed via its
     own close button so the theme's teardown (focus return, scroll lock, aria)
     runs normally. */
  function closeSearchDrawer() {
    var drawer = document.getElementById('search-drawer');
    if (!drawer || drawer.hidden) return;

    var closeButton = drawer.querySelector('[is="close-button"]');
    if (closeButton) {
      closeButton.click();
      log('Closed #search-drawer so in-page results are visible.');
    }
  }

  /**
   * Sets --fs-search-overlay-offset so the overlay starts below the search bar.
   *
   * #main's top edge is the bottom of everything above it - exact, with no
   * sections to enumerate. Only measurable because #main is hidden with
   * `visibility`, not `display: none`, so its layout box survives.
   *
   * --header-height cannot substitute: it excludes the announcement bar.
   */
  function updateOverlayOffset() {
    var offset = 0;

    var main = document.getElementById('main');
    if (main) {
      offset = main.getBoundingClientRect().top;
    } else {
      /* No #main on this page: take the lowest edge of the header-group sections
         that actually occupy space. */
      var sections = document.querySelectorAll('.shopify-section-group-header-group');
      Array.prototype.forEach.call(sections, function (section) {
        if (!section.offsetParent && section.offsetHeight === 0) return;

        var rect = section.getBoundingClientRect();
        if (rect.height <= 0) return;
        if (rect.bottom > offset) offset = rect.bottom;
      });
    }

    document.documentElement.style.setProperty(
      '--fs-search-overlay-offset',
      Math.max(offset, 0) + 'px'
    );
  }

  function showOverlay() {
    closeSearchDrawer();

    /* Class first, measure second: the promo banners are hidden BY this class, so
       measuring earlier reads a header that is about to get shorter. */
    document.body.classList.add(OPEN_CLASS);
    overlay.hidden = false;
    isOpen = true;

    updateOverlayOffset();
  }

  /* Checked against the DOM, not `isOpen`: after a reload with ?q= still in the
     address bar isOpen is false while the overlay may still be showing. */
  function overlayVisible() {
    return !!(overlay && !overlay.hidden);
  }

  function hideOverlay() {
    if (!overlay) return;
    overlay.hidden = true;
    document.body.classList.remove(OPEN_CLASS);
    isOpen = false;
  }

  var guardTimer = null;

  /* Watches for the SERP actually painting and reports if it never does. Fast
     Simon reads only location.search and CATEGORY_ID, never the pathname, so it
     is expected to paint wherever it is mounted. */
  function guardRender(term) {
    clearInterval(guardTimer);

    var deadline = Date.now() + RENDER_TIMEOUT_MS;

    guardTimer = setInterval(function () {
      var app = document.getElementById('fast-simon-serp-app');
      var painted =
        app &&
        ((app.shadowRoot && app.shadowRoot.childElementCount > 0) ||
          app.childElementCount > 0);

      if (painted) {
        clearInterval(guardTimer);
        log('SERP painted into the overlay.');
        return;
      }

      if (Date.now() > deadline) {
        clearInterval(guardTimer);

        log(
          'SERP did NOT paint within ' + RENDER_TIMEOUT_MS + 'ms.',
          '\n  #fast-simon-serp-app present:', !!app,
          '\n  its child count:', app ? app.childElementCount : 'n/a',
          '\n  shadowRoot:', app ? !!app.shadowRoot : 'n/a',
          '\n  ?q= in URL:', new URLSearchParams(window.location.search).get('q'),
          '\n  init.min.js in document:',
          !!document.querySelector('script[src*="static-grid.fastsimon.com"]'),
          '\n  STORE_UUID defined:', typeof window.STORE_UUID !== 'undefined'
        );

        if (NAVIGATE_ON_FAILURE) {
          window.location.assign(fallbackUrl(term));
        }
      }
    }, 250);
  }

  /* Search page and collections host their own SERP: swap ?q= and fire popstate
     to re-query it in place. Without this a mobile drawer search there would
     silently do nothing. */
  function searchInExistingSerp(term) {
    if (!document.getElementById('fast-simon-serp-app')) return false;

    closeSearchDrawer();
    window.history.replaceState({}, '', inPlaceUrl(term));
    window.dispatchEvent(new PopStateEvent('popstate'));
    log('Re-queried the SERP already on this page (no overlay needed).');
    return true;
  }

  function search(term) {
    if (!overlayAvailable() || !mountSerp()) {
      if (searchInExistingSerp(term)) return;

      log(
        'Overlay unavailable - cannot show results in place.',
        '\n  #fs-search-overlay:', !!document.getElementById('fs-search-overlay'),
        '\n  #fs-search-overlay-mount:',
        !!document.getElementById('fs-search-overlay-mount'),
        '\n  #fs-search-overlay-template:',
        !!document.getElementById('fs-search-overlay-template'),
        '\n  (all three are rendered by the fast-simon-search-overlay snippet,',
        'which is skipped when is_fast_simon_serp is true)'
      );

      if (NAVIGATE_ON_FAILURE) window.location.assign(fallbackUrl(term));
      return;
    }

    /* First open only - refining must not overwrite this with another search URL. */
    if (!isOpen) {
      urlBeforeSearch =
        window.location.pathname + window.location.search + window.location.hash;
      scrollBeforeSearch = window.scrollY;
    }

    /* push on first open so Back closes the results, replace when refining, so one
       search session is one history entry. */
    var method = isOpen ? 'replaceState' : 'pushState';
    window.history[method]({ fsSearch: true }, '', inPlaceUrl(term));
    window.dispatchEvent(new PopStateEvent('popstate'));

    showOverlay();
    guardRender(term);
  }

  function closeSearch() {
    clearInterval(guardTimer);

    if (!isOpen) return;
    hideOverlay();

    /* replaceState to the remembered URL rather than history.back(): there may be
       nothing to go back to, and the SERP pushes its own entries for facets and
       paging, so one step back could land inside the search. */
    if (urlBeforeSearch !== null) {
      window.history.replaceState({}, '', urlBeforeSearch);
      urlBeforeSearch = null;
    }

    /* body's `overflow: hidden` was only just dropped - wait a frame or the
       browser clamps the scroll target. */
    if (scrollBeforeSearch !== null) {
      var targetScroll = scrollBeforeSearch;
      scrollBeforeSearch = null;
      requestAnimationFrame(function () {
        window.scrollTo(0, targetScroll);
      });
    }

    /* Reopening search should start fresh, not re-trigger on stale text. */
    var inputs = document.querySelectorAll(INPUT_SELECTOR);
    Array.prototype.forEach.call(inputs, function (input) {
      input.value = '';
    });
  }

  /**
   * Called when the search box is cleared. Three real states:
   *   isOpen           -> closeSearch(): restores URL, scroll and input.
   *   overlay visible  -> reloaded mid-search, so isOpen lies; hide it directly
   *                       (closeSearch would bail on !isOpen).
   *   on SEARCH_PAGE   -> real navigation home; no overlay to hide.
   * The trailing ?q= strip covers a reload on the home page, where none of the
   * three match but a stale query is still in the URL.
   */
  function leaveSearch() {
    if (isOpen) {
      closeSearch();
      return;
    }

    if (overlayVisible()) {
      hideOverlay();
    }

    if (onSearchPage()) {
      window.location.assign(HOME_PAGE);
      return;
    }

    if (currentQuery()) {
      window.history.replaceState({}, '', window.location.pathname);
    }
  }

  var timer = null;

  function schedule(input) {
    clearTimeout(timer);

    var cleared = input.value.trim().length === 0;

    timer = setTimeout(function () {
      var term = input.value.trim();

      if (!term) {
        leaveSearch();
        return;
      }

      /* Bails after the clear branch above, not before it: clearing the box has
         to keep closing an open result set even when typing cannot open one.
         That is the only way to dismiss results on mobile - the overlay's close
         button is hidden below 1149px (see 'fast-simon-search-overlay'), on the
         grounds that emptying the field already returns the shopper home. */
      if (!SEARCH_WHILE_TYPING) return;

      if (term.length < MIN_CHARS) return;

      /* Already showing this search. Also what stops the popstate we fire from
         looping back into another search. */
      if (isOpen && currentQuery() === term) return;
      if (!isOpen && onSearchPage() && currentQuery() === term) return;

      search(term);
    }, cleared ? CLEAR_DELAY_MS : DEBOUNCE_MS);
  }

  function onSubmit(event, input) {
    /* Submitting means "search now"; the form's hidden Shopify-search fields mean
       nothing to Fast Simon. */
    event.preventDefault();
    clearTimeout(timer);

    var term = input.value.trim();

    if (!term) {
      leaveSearch();
      return;
    }

    search(term);
  }

  function bind() {
    var inputs = document.querySelectorAll(INPUT_SELECTOR);

    Array.prototype.forEach.call(inputs, function (input) {
      if (input.dataset.fsSearch) return;
      input.dataset.fsSearch = 'true';

      input.addEventListener('input', function () {
        schedule(input);
      });

      var form = input.closest('form');
      if (form) {
        form.addEventListener('submit', function (event) {
          onSubmit(event, input);
        });
      }
    });
  }

  function init() {
    bind();

    var closeButton = document.getElementById('fs-search-overlay-close');
    if (closeButton) closeButton.addEventListener('click', closeSearch);

    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape' && isOpen) closeSearch();
    });

    /* Real history moves only - our own popstate always carries ?q=. No URL
       restore here; the browser has already set the address bar. */
    window.addEventListener('popstate', function () {
      if (isOpen && !currentQuery()) {
        hideOverlay();
        urlBeforeSearch = null;
      }
    });

    /* The header changes height across the 1149px breakpoint and when the
       announcement bar wraps. */
    var resizeTimer = null;
    window.addEventListener('resize', function () {
      if (!isOpen) return;
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(updateOverlayOffset, 150);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* Theme editor re-renders the header section; re-bind the new input. */
  document.addEventListener('shopify:section:load', bind);
})();
